import { cn, Skeleton } from "@aircall/ds";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
} from "react";

export interface SkeletonNoteProps extends ComponentPropsWithoutRef<"div"> {
  "data-test"?: string;
  quantity?: number;
}

type SkeletonTextProps = {
  quantity?: number;
  dataTest: string;
};

function SkeletonText({ quantity = 3, dataTest }: SkeletonTextProps) {
  if (quantity < 1) {
    return null;
  }

  return (
    <div
      data-slot="skeleton-note-text"
      className="flex w-full flex-col gap-0.5"
      data-test={`${dataTest}-text`}
    >
      {Array.from({ length: quantity }, (_, index) => {
        const isLastLine = index === quantity - 1;
        return (
          <Skeleton
            key={`${dataTest}-line-${index}`}
            data-slot="skeleton-note-line"
            // `cn`/tailwind-merge keeps the last width: `w-2/5` wins on the
            // final line, `w-full` otherwise — no full-string ternary.
            className={cn("h-[14px] w-full", isLastLine && "w-2/5")}
            data-test={`${dataTest}-line-${index}`}
          />
        );
      })}
    </div>
  );
}

export const SkeletonNote = forwardRef<HTMLDivElement, SkeletonNote.Props>(
  (
    componentProps: SkeletonNote.Props,
    forwardRef: ForwardedRef<HTMLDivElement>
  ) => {
    const {
      "data-test": dataTest = "skeleton-note",
      quantity = 2,
      className,
      ...props
    } = componentProps;

    return (
      <div
        ref={forwardRef}
        data-slot="skeleton-note"
        className={cn("flex w-full flex-col gap-1", className)}
        data-test={dataTest}
        {...props}
      >
        <div
          data-slot="skeleton-note-header"
          className="flex items-center justify-between gap-0.5"
        >
          <div
            data-slot="skeleton-note-author"
            className="flex items-center gap-0.5"
          >
            <Skeleton className="size-6 rounded-lg" />
            <Skeleton className="h-[14px] w-20" />
          </div>
          <Skeleton className="h-[14px] w-[100px]" />
        </div>
        <SkeletonText quantity={quantity} dataTest={dataTest} />
      </div>
    );
  }
);
SkeletonNote.displayName = "SkeletonNote";

export namespace SkeletonNote {
  export type Props = SkeletonNoteProps;
}
